// Stack types — enums, configs, and template definitions.
// The API collection is still called "applications"; the UI calls them "stacks".

export enum StackType {
  DATA_ENGINEERING = "data_engineering",
  ANALYTICAL = "analytical",
  FRONTEND_BACKEND = "frontend_backend",
}

export enum CloudProvider {
  AWS = "aws",
  GCP = "gcp",
  AZURE = "azure",
  SELF_HOSTED = "self_hosted",
  RUSHA_HOSTED = "rusha_hosted",
}

export enum StackStatus {
  DRAFT = "draft",
  PROVISIONING = "provisioning",
  VCS_PROVISIONED = "vcs_provisioned",
  PROVISIONING_FAILED = "provisioning_failed",
  BUILDING = "building",
  DEPLOYING = "deploying",
  RUNNING = "running",
  STOPPED = "stopped",
  FAILED = "failed",
  DELETED = "deleted",
}

// ── Data engineering stack options (full pipeline: ingest → process → store → catalog → query → orchestrate → BI) ──

export type IngestionTool =
  | "airbyte"
  | "debezium_kafka"
  | "kafka"
  | "meltano"
  // AWS managed
  | "kinesis"
  | "msk"
  | "aws_glue_crawler"
  | "dms";
export type ProcessingTool =
  | "dbt"
  | "spark"
  | "flink"
  | "dbt_duckdb"
  // AWS managed
  | "aws_glue_etl"
  | "emr_spark"
  | "emr_flink";
export type TableFormat = "iceberg" | "delta_lake" | "hudi" | "none";
export type DataCatalog =
  | "hive_metastore"
  | "polaris"
  | "nessie"
  | "none"
  // AWS managed
  | "aws_glue_catalog";
export type QueryEngineTool =
  | "trino"
  | "spark_sql"
  | "duckdb"
  | "postgres"
  // AWS managed
  | "athena"
  | "redshift";
export type OrchestrationTool = "dagster" | "airflow" | "prefect" | "mwaa";
export type BiTool =
  "superset" | "metabase" | "grafana" | "beaver" | "quicksight";

export interface DataEngineeringConfig {
  ingestion: IngestionTool;
  processing: ProcessingTool;
  tableFormat: TableFormat;
  catalog: DataCatalog;
  queryEngine: QueryEngineTool;
  orchestration: OrchestrationTool;
  biTool: BiTool;
}

// ── Analytical stack options (OLAP + BI — reads from lake, OLAP store, or DB) ──

export type OlapEngine =
  | "clickhouse"
  | "starrocks"
  | "druid"
  | "pinot"
  | "duckdb"
  // AWS managed
  | "redshift";
export type BiDashboard =
  "metabase" | "superset" | "grafana" | "redash" | "quicksight";
export type AnalyticalSourceType =
  "iceberg_catalog" | "olap_load" | "kafka_stream" | "relational_db";
export type AnalyticalDatabase =
  | "postgresql"
  | "mysql"
  | "bigquery"
  | "sqlite"
  // AWS managed
  | "redshift"
  | "aurora_postgres";

export interface AnalyticalConfig {
  sourceType: AnalyticalSourceType;
  olapEngine: OlapEngine | null; // null when sourceType = "iceberg_catalog" or "relational_db"
  biTool: BiDashboard;
  queryEngine?: QueryEngineTool; // for iceberg_catalog — typically "trino"
  database?: AnalyticalDatabase; // for relational_db source
}

// ── Frontend/backend stack options ────────────────────────────────────────────

export type FrontendFramework =
  "react" | "nextjs" | "vue" | "angular" | "svelte" | "remix";
export type BackendFramework =
  | "fastapi"
  | "django"
  | "flask"
  | "litestar"
  | "nestjs"
  | "express"
  | "fastify"
  | "hono"
  | "remix"
  | "spring_boot"
  | "quarkus"
  | "micronaut"
  | "go_fiber"
  | "gin"
  | "echo"
  | "chi";
export type DatabaseEngine = "postgresql" | "mysql" | "mongodb" | "sqlite";
export type CacheLayer = "redis" | "memcached";
export type MessageQueue = "rabbitmq" | "kafka";

export interface FrontendBackendConfig {
  frontend: FrontendFramework;
  backend: BackendFramework;
  database: DatabaseEngine;
  cache?: CacheLayer;
  messageQueue?: MessageQueue;
}

export type StackConfig =
  DataEngineeringConfig | AnalyticalConfig | FrontendBackendConfig;

// ── Stack template (defined in the API, not the UI) ───────────────────────────

export type TemplateComplexity = "low" | "medium" | "high";

/** Primary implementation language — used for filtering in the UI */
export type PrimaryLanguage =
  "java" | "python" | "typescript" | "go" | "rust" | "multi";

/** Service specification from template */
export interface ServiceSpec {
  name: string;
  type: string;
  port?: number;
  image?: string;
  internetFacing: boolean;
  resources?: {
    cpu: string;
    memory: string;
  };
  storage?: string;
}

/** Configuration for service resources in the wizard */
export interface ServiceConfig {
  name: string;
  type: string;
  cpu: number;
  memory: number;
  storage: number;
  replicas: number;
  internetFacing: boolean;
}

export interface StackTemplate {
  id: number;
  systemType: StackType;
  slug: string;
  name: string;
  description: string;
  useCase: string;
  tags: string[];
  complexity: TemplateComplexity;
  stackConfig: StackConfig;
  /** Primary backend language — omitted for multi-language data stacks */
  language?: PrimaryLanguage;
  /** Services defined in this template */
  services?: ServiceSpec[];
}
